import { Navigate, Outlet } from '@tanstack/react-router';
import type { SearchMiddleware } from '@tanstack/router-core';
import { useIndexQuery } from 'api';
import LoadingScreen from 'components/LoadingScreen';
import { useCurrentLabOrThrow, useCurrentRole, useCurrentUser } from 'hooks/slices';
import { includes, isEqual } from 'lodash';
import type { ReactNode } from 'react';
import { useAuthStore } from 'stores/authStore';
import type { Pharmacy, PharmacyRole, Role, RoleWithResource, User } from 'types/models';
import type { PharmacySettings } from 'types/models/pharmacy_settings';
import {
  hasNoWebAccess,
  isFaksRole,
  isGroupRole,
  isLabRole,
  isPharmacyRole,
} from 'utils/rolesUtils';
import { z } from 'zod';

type CurrentUser = User & { roles: RoleWithResource[] };

const isFaksAdminRole = (currentRole: RoleWithResource | null) =>
  !!currentRole && isFaksRole(currentRole) && ['admin', 'super_admin'].includes(currentRole.kind);

export const canAccessGroupPages = (_currentUser: User | null, currentRole: Role | null) =>
  !!currentRole && isGroupRole(currentRole);

export const canAccessLabPages = (_currentUser: User | null, currentRole: Role | null) =>
  !!currentRole && isLabRole(currentRole) && !hasNoWebAccess(currentRole.kind);

export const canAccessPharmacyPages = (
  _currentUser: User | null,
  currentRole: RoleWithResource | null
) => !!currentRole && isPharmacyRole(currentRole) && isPharmacyOnboardingComplete(currentRole);

export const canAccessOnboardingPages = (
  currentUser: User | null,
  currentRole: RoleWithResource | null
) =>
  !!currentUser &&
  (!currentRole ||
    (currentRole.resource_type === 'Pharmacy' && !isPharmacyOnboardingComplete(currentRole)));

export const canAccessOnboarderPages = (
  _currentUser: CurrentUser | null,
  currentRole: RoleWithResource | null
) => isFaksAdminRole(currentRole) || (!!currentRole && currentRole.kind === 'onboarder');

export const canAccessAdminPages = (
  _currentUser: CurrentUser | null,
  currentRole: RoleWithResource | null
) => isFaksAdminRole(currentRole);

const unverifiedPharmacyRoles = [
  'pharmacy_owner_unverified_phone',
  'pharmacy_owner_unverified_email',
  'no_access',
];

export const isPharmacyOnboardingComplete = (
  userRole: PharmacyRole & { resource: Pharmacy }
): boolean => userRole.resource.verified && !includes(unverifiedPharmacyRoles, userRole.kind);

export const zodPage = z.number().int().positive().optional().default(1);
export const zodPathParamId = z.coerce.number().int().positive();
const zodId = z.number().int().positive();
export const zodOptionalId = zodId.optional();
export const zodOptionalIdArray = z.tuple([zodId], zodId).optional();

export const zodSort = <U extends string, T extends Readonly<[U, ...U[]]>>(allowedSorts: T) =>
  z.object({ by: z.enum(allowedSorts), direction: z.enum(['asc', 'desc']) }).optional();
export const zodOptionalDate = z.string().date().optional();
export const zodOptionalDateRange = z
  .object({ from: zodOptionalDate, to: zodOptionalDate })
  .optional();
export const zodOptionalString = z.string().optional();

export const pageResetMiddleWare: SearchMiddleware<{ page?: number }> = ({ search, next }) => {
  const nextSearch = next(search);
  return { ...nextSearch, page: hasFilterChange(search, nextSearch) ? 1 : nextSearch.page };
};

const hasFilterChange = (
  { page, ...search }: Record<string, unknown>,
  { page: nextPage, ...nextSearch }: Record<string, unknown>
) => {
  // next search may contain undefined values, that are stripped out from the URL at the end
  const normalizedNextSearch = Object.fromEntries(
    Object.entries(nextSearch).filter(([_key, value]) => value !== undefined)
  );
  return !isEqual(search, normalizedNextSearch);
};

const NavigateToRoot = () => {
  const redirectionUrl = window.location.pathname + window.location.search;
  return (
    <Navigate
      to="/"
      search={{
        redirection_url: !redirectionUrl.startsWith('/login') ? redirectionUrl : undefined,
      }}
      replace
    />
  );
};

type GuardPredicate = (
  currentUser: CurrentUser | null,
  currentRole: RoleWithResource | null
) => boolean;

type GuardDecision = 'loading' | 'denied' | 'allowed';

/**
 * Pure decision for a route guard, extracted so the impersonation race is
 * covered without mounting (see routeUtils.test.ts).
 *
 * When authenticated but the current user query has not resolved yet — e.g.
 * right after impersonation clears the query cache — we must WAIT for the data,
 * not evaluate the predicate. Otherwise a transiently-null role fails the
 * predicate and the guard bounces to root, clobbering the ongoing navigation
 * (an admin impersonating onto a trade page gets redirected away before
 * landing). FKS-8438
 */
export const resolveGuardDecision = (
  isAuthenticated: boolean,
  currentUser: CurrentUser | null,
  currentRole: RoleWithResource | null,
  predicate: GuardPredicate
): GuardDecision => {
  if (isAuthenticated && !currentUser) return 'loading';
  if (!predicate(currentUser, currentRole)) return 'denied';
  return 'allowed';
};

export const WithEnsure = (predicate: GuardPredicate, component: ReactNode) => {
  const authToken = useAuthStore((state) => state.userToken);
  const currentUser = useCurrentUser();
  const currentRole = useCurrentRole();

  switch (resolveGuardDecision(!!authToken, currentUser, currentRole, predicate)) {
    case 'loading':
      return <LoadingScreen />;
    case 'denied':
      return <NavigateToRoot />;
    case 'allowed':
      return component;
  }
};

type FeatureAccess = 'loading' | 'denied' | 'granted';

export const WithFeatureAccess = (access: FeatureAccess, component: ReactNode) => {
  switch (access) {
    case 'loading':
      return <LoadingScreen />;
    case 'denied':
      return <NavigateToRoot />;
    case 'granted':
      return component;
  }
};

export const WithPharmacySettingEnabled = (
  isEnabled: (setting: PharmacySettings) => boolean,
  component: ReactNode
) => {
  const { data } = useIndexQuery('pharmacies/pharmacies/current/setting', {});
  const access: FeatureAccess = !data
    ? 'loading'
    : isEnabled(data.pharmacy_setting)
      ? 'granted'
      : 'denied';
  return WithFeatureAccess(access, component);
};

export const WithExpiredFeatureCheck = () => {
  const lab = useCurrentLabOrThrow();
  const useExpiredLabSettingQuery = useIndexQuery(
    { route: 'expired/shared/labs/:id/config', pathParams: { id: lab.id } },
    {}
  );
  const expiredLabSetting = useExpiredLabSettingQuery.data?.expired_lab_config;

  if (!expiredLabSetting) return <LoadingScreen />;

  if (!expiredLabSetting.is_feature_enabled) {
    return <NavigateToRoot />;
  }

  return <Outlet />;
};
